import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Toggle } from '../../Toggle';
import { aiAPI } from '../../../services/api';
import { recurringActionService } from '../../../services/recurringActionService';
import { parseUTCTimestamp } from '../../../utils/dateUtils';
import type { RecurringAction } from '../../../types';

interface ContextDigestViewerProps {
  digest: string | null | undefined;
  updatedTime: string | null | undefined;
  onRefreshUser: () => Promise<void>;
}

export const ContextDigestViewer: React.FC<ContextDigestViewerProps> = ({
  digest,
  updatedTime,
  onRefreshUser,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cronAction, setCronAction] = useState<RecurringAction | null>(null);
  const [isTogglingCron, setIsTogglingCron] = useState(false);

  useEffect(() => {
    loadCronState();
  }, []);

  const loadCronState = async () => {
    try {
      const actions = await recurringActionService.list(true);
      const existing = actions.find(a => a.system_action === 'weekly_digest');
      setCronAction(existing || null);
    } catch {
      // Silently fail
    }
  };

  const handleRunReview = async () => {
    setIsRunning(true);
    setError(null);
    try {
      await aiAPI.runContextReview();
      await onRefreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run context review');
    } finally {
      setIsRunning(false);
    }
  };

  const handleToggleCron = async () => {
    setIsTogglingCron(true);
    setError(null);
    try {
      if (cronAction) {
        if (cronAction.status === 'active') {
          const updated = await recurringActionService.pause(cronAction.id);
          setCronAction(updated);
        } else {
          const updated = await recurringActionService.resume(cronAction.id);
          setCronAction(updated);
        }
      } else {
        const created = await recurringActionService.create({
          name: 'Weekly Context Digest',
          prompt: 'Generate context digest',
          cron_expression: '0 6 * * 0',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          system_action: 'weekly_digest',
        });
        setCronAction(created);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle weekly digest schedule');
    } finally {
      setIsTogglingCron(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = parseUTCTimestamp(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const cronIsActive = cronAction?.status === 'active';

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Context Digest
        </label>
        <div className="flex items-center gap-2">
          <Toggle label="Weekly" checked={cronIsActive} onChange={handleToggleCron} disabled={isTogglingCron} />
          <button
            type="button"
            onClick={handleRunReview}
            disabled={isRunning}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRunning ? 'animate-spin' : ''}`} />
            {isRunning ? 'Running...' : 'Run Context Review'}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        AI-synthesized summary of your recent activity, patterns, and focus areas.
        Used to personalize assistant responses.
        {cronIsActive && ' Auto-runs every Sunday at 6 AM.'}
      </p>

      {error && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
          <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {digest ? (
        <div>
          <div className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {digest}
          </div>
          {updatedTime && (
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Last updated: {formatDate(updatedTime)}
            </p>
          )}
        </div>
      ) : (
        <div className="w-full px-3 py-4 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md text-gray-400 dark:text-gray-500 text-center italic">
          No digest yet. Click "Run Context Review" to generate one.
        </div>
      )}
    </div>
  );
};
